import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import './Dashboard.css';

const RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
];
const MODE_COLORS = { upi: '#3fae7a', cash: '#ef6f8f', card: '#f2b544' };
const money = (v) => `₹${Math.round(Number(v) || 0).toLocaleString('en-IN')}`;
const niceDate = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
const niceTime = (utc) => (utc ? new Date(`${String(utc).replace(' ', 'T')}Z`).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '');
const hourLabel = (h) => (h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`);
const go = (tab) => window.dispatchEvent(new CustomEvent('icecream-navigate', { detail: tab }));

function useCountUp(value, ms = 500) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = performance.now(); const a = from.current; const b = Number(value) || 0;
    let raf;
    const step = (t) => { const k = Math.min(1, (t - start) / ms); setShown(a + (b - a) * (1 - Math.pow(1 - k, 3))); if (k < 1) raf = requestAnimationFrame(step); else from.current = b; };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return shown;
}

function Change({ now, before, invert }) {
  if (!(before > 0)) return <span className="db-chg is-flat">no earlier data</span>;
  const pct = ((now - before) / before) * 100;
  const up = pct >= 0;
  const good = invert ? !up : up;
  return <span className={`db-chg ${good ? 'is-up' : 'is-down'}`}>{up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%</span>;
}

function Kpi({ cls, label, value, money: isMoney = true, children }) {
  const v = useCountUp(value);
  return (
    <div className={`db-kpi ${cls}`}>
      <span className="db-kpi-label">{label}</span>
      <strong className="db-kpi-value">{isMoney ? money(v) : Math.round(v).toLocaleString('en-IN')}</strong>
      <div className="db-kpi-foot">{children}</div>
    </div>
  );
}

export default function DashboardPage() {
  const [range, setRange] = useState('today');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [bestBy, setBestBy] = useState('revenue');
  const [bestGroup, setBestGroup] = useState('all');
  const [modeFilter, setModeFilter] = useState(null);
  const [hover, setHover] = useState(null);

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try { setData(await api.get(`/dashboard?range=${range}`)); setError(''); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => load(true), 60000); return () => clearInterval(t); }, [load]);

  const best = useMemo(() => {
    if (!data) return [];
    return data.best.filter((b) => bestGroup === 'all' || b.group === bestGroup)
      .sort((a, b) => b[bestBy] - a[bestBy]).slice(0, 6);
  }, [data, bestBy, bestGroup]);

  const alertGroups = useMemo(() => {
    if (!data) return [];
    const m = {};
    data.alerts.forEach((a) => { (m[a.supplier] = m[a.supplier] || []).push(a); });
    return Object.entries(m).sort((a, b) => b[1].length - a[1].length);
  }, [data]);

  const setRent = async () => {
    const v = window.prompt('Monthly rent (₹). Used for the break-even figure.', String(data.break_even.rent_monthly || ''));
    if (v === null) return;
    try { await api.post('/dashboard/settings', { monthly_rent: Number(v) || 0 }); load(true); } catch (e) { alert(e.message); }
  };

  if (loading && !data) return <div className="db-root"><div className="db-loading">Loading dashboard…</div></div>;
  if (!data) return (
    <div className="db-root"><div className="db-card db-error"><strong>Couldn't load the dashboard</strong><p>{error}</p><button type="button" className="db-btn" onClick={() => load()}>Try again</button></div></div>
  );

  const { totals, previous, profit, break_even: be, shift } = data;
  const periodText = data.days === 1 ? new Date(`${data.from}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })
    : `${niceDate(data.from)} – ${niceDate(data.to)}`;
  const prevText = data.days === 1 ? (data.range === 'yesterday' ? 'vs day before' : 'vs yesterday') : `vs previous ${data.days} days`;
  const maxChart = Math.max(1, ...data.chart.map((c) => c.amount));
  const hasChart = data.chart.some((c) => c.amount > 0);
  const progress = be.needed_sales ? Math.min(100, (totals.sales / be.needed_sales) * 100) : 0;
  const reached = be.needed_sales != null && totals.sales >= be.needed_sales;
  const recent = data.recent.filter((b) => !modeFilter || b.modes.includes(modeFilter)).slice(0, 8);
  const hour = new Date(Date.now() + 5.5 * 3600000).getUTCHours();
  const greet = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="db-root">
      <header className="db-head">
        <div>
          <span className="db-eyebrow">Overview</span>
          <h1>{greet} 👋</h1>
          <p>{periodText}{data.range === 'today' ? ' · updates every minute' : ''}</p>
        </div>
        <button type="button" className="db-command-trigger" onClick={() => window.dispatchEvent(new Event('icecream-palette'))} aria-label="Search pages and actions">
          <span className="db-command-search-icon">⌕</span>
          <span>Quick search</span>
          <kbd>{/Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘ K' : 'Ctrl K'}</kbd>
        </button>
        <div className="db-range" role="tablist">
          {RANGES.map((r) => (
            <button key={r.id} type="button" role="tab" aria-selected={range === r.id} className={range === r.id ? 'is-on' : ''} onClick={() => setRange(r.id)}>{r.label}</button>
          ))}
          <button type="button" className="db-refresh" onClick={() => load()} title="Refresh">↻</button>
        </div>
      </header>

      <section className="db-quick">
        <button type="button" onClick={() => go('billing')}>🧾 New bill</button>
        <button type="button" onClick={() => go('purchases')}>📦 Add purchase</button>
        <button type="button" onClick={() => go('expenses')}>💸 Add expense</button>
        <button type="button" onClick={() => go('shifts')}>🔄 Shift handover</button>
        <button type="button" onClick={() => go('stockcount')}>📋 Count stock</button>
        <button type="button" onClick={() => go('profit')}>📈 Profit &amp; reports</button>
      </section>

      <section className="db-kpis">
        <Kpi cls="is-sales" label="Sales" value={totals.sales}>
          <span>{totals.bills} bills · avg {money(totals.avg)}</span>
          <Change now={totals.sales} before={previous.sales} /> <small>{prevText}</small>
        </Kpi>
        <Kpi cls="is-profit" label="Gross profit" value={profit.gross}>
          <span>{profit.sales > 0 ? `${((profit.gross / profit.sales) * 100).toFixed(1)}% margin` : '—'} · product cost {money(profit.cogs)}</span>
        </Kpi>
        <Kpi cls={profit.net >= 0 ? 'is-net' : 'is-loss'} label="Final profit" value={profit.net}>
          <span>after EB {money(profit.eb)} · salary {money(profit.salary)} · expenses {money(profit.expenses)}</span>
          {profit.sales > 0 && <b>{((profit.net / profit.sales) * 100).toFixed(1)}% of sales</b>}
        </Kpi>
        <div className={`db-kpi db-be ${reached ? 'is-reached' : ''}`}>
          <span className="db-kpi-label">Break-even {data.days === 1 ? 'today' : 'this period'}</span>
          {be.needed_sales == null ? (
            <strong className="db-kpi-value db-small">Needs sales data</strong>
          ) : (
            <>
              <strong className="db-kpi-value">{Math.round(progress)}%</strong>
              <div className="db-be-bar"><i style={{ width: `${progress}%` }} /></div>
              <div className="db-kpi-foot">
                <span>{money(totals.sales)} of {money(be.needed_sales)} needed {reached ? '✅' : ''}</span>
                <span>{be.rent_monthly ? `incl. rent ${money(be.rent_share)} · after rent ${money(be.after_rent)}` : <button type="button" className="db-link" onClick={setRent}>Add monthly rent for a true figure</button>}</span>
                {be.rent_monthly > 0 && <button type="button" className="db-link" onClick={setRent}>Rent {money(be.rent_monthly)}/month ✎</button>}
              </div>
            </>
          )}
        </div>
      </section>

      {(profit.uncosted > 0 || profit.unmarked > 0) && (
        <div className="db-note">
          {profit.uncosted > 0 && <span>⚠ {money(profit.uncosted)} of sales have no purchase cost yet, so profit is shown higher than it really is. </span>}
          {profit.unmarked > 0 && <span>⚠ Attendance not marked for {profit.unmarked} staff-day(s); their salary isn't counted. <button type="button" className="db-link" onClick={() => go('payroll')}>Mark attendance</button></span>}
        </div>
      )}

      <section className="db-grid-2">
        <div className="db-card">
          <div className="db-card-head">
            <div><h3>Sales {data.days === 1 ? 'by hour' : 'by day'}</h3><p>Hover a bar for details</p></div>
            <strong className="db-accent">{money(totals.sales)}</strong>
          </div>
          {!hasChart ? <div className="db-empty">No sales in this period yet.</div> : (
            <>
              <div className="db-chart" onMouseLeave={() => setHover(null)}>
                {data.chart.map((c, i) => (
                  <div key={c.label} className="db-col" onMouseEnter={() => setHover(i)} onClick={() => setHover(i)}>
                    <div className={`db-bar ${c.amount > 0 ? 'has' : ''} ${hover === i ? 'is-hover' : ''}`} style={{ height: `${c.amount > 0 ? Math.max(4, (c.amount / maxChart) * 100) : 2}%` }} />
                  </div>
                ))}
                {hover != null && data.chart[hover] && (
                  <div className="db-tip" style={{ left: `${((hover + 0.5) / data.chart.length) * 100}%` }}>
                    <b>{data.days === 1 ? `${hourLabel(data.chart[hover].label)} – ${hourLabel((data.chart[hover].label + 1) % 24)}` : niceDate(data.chart[hover].label)}</b>
                    <span>{money(data.chart[hover].amount)} · {data.chart[hover].bills} bills</span>
                    {data.chart[hover].amount > 0 && <span>UPI {money(data.chart[hover].upi)} · Cash {money(data.chart[hover].cash)}{data.chart[hover].card ? ` · Card ${money(data.chart[hover].card)}` : ''}</span>}
                  </div>
                )}
              </div>
              <div className="db-axis">
                {data.days === 1
                  ? ['12 AM', '6 AM', '12 PM', '6 PM', '11 PM'].map((l) => <span key={l}>{l}</span>)
                  : [data.chart[0], data.chart[Math.floor(data.chart.length / 2)], data.chart[data.chart.length - 1]].map((c) => <span key={c.label}>{niceDate(c.label)}</span>)}
              </div>
            </>
          )}
        </div>

        <div className="db-card">
          <div className="db-card-head"><div><h3>Payments</h3><p>Tap one to filter recent bills</p></div></div>
          {data.payments.length === 0 ? <div className="db-empty">No payments yet.</div> : (
            <>
              <div className="db-paybar">
                {data.payments.map((p) => <i key={p.mode} style={{ width: `${p.percent}%`, background: MODE_COLORS[p.mode] || '#999' }} />)}
              </div>
              {data.payments.map((p) => (
                <button key={p.mode} type="button" className={`db-payrow ${modeFilter === p.mode ? 'is-on' : ''}`} onClick={() => setModeFilter(modeFilter === p.mode ? null : p.mode)}>
                  <span><i style={{ background: MODE_COLORS[p.mode] || '#999' }} />{p.mode.toUpperCase()}</span>
                  <span><b>{money(p.amount)}</b> <small>{p.percent}%</small></span>
                </button>
              ))}
            </>
          )}
          <div className="db-recent">
            <div className="db-recent-head"><b>Recent bills{modeFilter ? ` · ${modeFilter.toUpperCase()}` : ''}</b><button type="button" className="db-link" onClick={() => go('billhistory')}>All bills →</button></div>
            {recent.map((b) => (
              <div key={b.id} className={`db-recent-row ${b.status === 'cancelled' ? 'is-cancelled' : ''}`}>
                <span>{b.bill_no}<small>{niceTime(b.bill_date)}</small></span>
                <span>{b.status === 'cancelled' ? 'Cancelled' : b.modes.map((m) => m.toUpperCase()).join(' + ')}</span>
                <b>{money(b.total_amount)}</b>
              </div>
            ))}
            {recent.length === 0 && <div className="db-empty db-small">No bills to show.</div>}
          </div>
        </div>
      </section>

      <section className="db-grid-3">
        <div className="db-card">
          <div className="db-card-head">
            <div><h3>Best sellers</h3></div>
            <div className="db-seg">
              {['qty', 'revenue', 'profit'].map((k) => <button key={k} type="button" className={bestBy === k ? 'is-on' : ''} onClick={() => setBestBy(k)}>{k === 'qty' ? 'Qty' : k === 'revenue' ? '₹ Sales' : 'Profit'}</button>)}
            </div>
          </div>
          <div className="db-seg db-seg-sm">
            {['all', 'Ice Bay', "Hendy's Kitchen"].map((g) => <button key={g} type="button" className={bestGroup === g ? 'is-on' : ''} onClick={() => setBestGroup(g)}>{g === 'all' ? 'All' : g}</button>)}
          </div>
          {best.length === 0 ? <div className="db-empty">No sales in this period.</div> : (
            <ol className="db-best">
              {best.map((b) => {
                const top = best[0][bestBy] || 1;
                return (
                  <li key={b.id}>
                    <div className="db-best-row">
                      <span>{b.name}<small>{b.group}</small></span>
                      <b>{bestBy === 'qty' ? `${b.qty}${b.weighted ? ' g' : ''}` : money(b[bestBy])}</b>
                    </div>
                    <div className="db-best-bar"><i style={{ width: `${Math.max(3, (b[bestBy] / top) * 100)}%` }} /></div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="db-card">
          <div className="db-card-head">
            <div><h3>Stock alerts</h3><p>{data.alerts.filter((a) => a.level === 'out').length} out of stock · {data.alerts.filter((a) => a.level === 'low').length} low</p></div>
            <button type="button" className="db-btn db-btn-sm" onClick={() => go('stockcount')}>Check stock</button>
          </div>
          {alertGroups.length === 0 ? <div className="db-empty">✓ Nothing out of stock.</div> : (
            <div className="db-alerts">
              {alertGroups.map(([sup, items]) => (
                <div key={sup} className="db-alert-group">
                  <div className="db-alert-sup"><b>{sup}</b><button type="button" className="db-link" onClick={() => go('purchases')}>+ Add purchase</button></div>
                  {items.slice(0, 6).map((a) => (
                    <div key={a.id} className="db-alert-row">
                      <span>{a.name}</span>
                      <span className={a.level === 'out' ? 'is-out' : 'is-low'}>{a.level === 'out' ? 'Out' : `${a.stock} ${a.unit} left`}</span>
                    </div>
                  ))}
                  {items.length > 6 && <div className="db-more">+{items.length - 6} more</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="db-card">
          <div className="db-card-head"><div><h3>Shift</h3></div><button type="button" className="db-btn db-btn-sm" onClick={() => go('shifts')}>Open</button></div>
          {!shift ? <div className="db-empty">Shift handover not set up.</div> : (
            <div className="db-shift">
              {shift.open
                ? <div className="db-shift-open"><span className="db-dot is-live" />{shift.open.name} shift open<small>since {niceTime(shift.open.opened_at)}{shift.open.opened_by ? ` by ${shift.open.opened_by}` : ''} · opening cash {money(shift.open.opening_cash)}</small></div>
                : <div className="db-shift-open"><span className="db-dot" />No shift open<small>Start one in Shift Handover before billing.</small></div>}
              {shift.last && (
                <div className="db-shift-last">
                  <b>Last closed: {shift.last.name}, {niceDate(shift.last.date)}</b>
                  <div className="db-shift-diffs">
                    <span className={shift.last.cash_diff < 0 ? 'is-bad' : shift.last.cash_diff > 0 ? 'is-warn' : 'is-good'}>Cash {shift.last.cash_diff == null ? '—' : shift.last.cash_diff === 0 ? 'matched' : `${shift.last.cash_diff > 0 ? '+' : '−'}${money(Math.abs(shift.last.cash_diff))}`}</span>
                    <span className={shift.last.upi_diff < 0 ? 'is-bad' : shift.last.upi_diff > 0 ? 'is-warn' : 'is-good'}>UPI {shift.last.upi_diff == null ? '—' : shift.last.upi_diff === 0 ? 'matched' : `${shift.last.upi_diff > 0 ? '+' : '−'}${money(Math.abs(shift.last.upi_diff))}`}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {data.audit.length > 0 && (
            <div className="db-activity">
              <b>Recent changes</b>
              {data.audit.map((a, i) => (
                <div key={i} className="db-activity-row"><span>{a.action}{a.reference ? ` · ${a.reference}` : ''}</span><small>{a.username || ''} · {niceTime(a.created_at)}</small></div>
              ))}
              <button type="button" className="db-link" onClick={() => go('auditlog')}>Audit log →</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
